// Lazy loader for the Google Maps JavaScript API (Places library).
//
// The Maps API is a large, render-blocking dependency (hundreds of KB of JS)
// that is only needed on the handful of pages that actually draw a map or an
// address autocomplete — the property map, the single-listing page, the
// configurator's address step, the renovation-quote form and the agency
// landing. It is NOT needed on the home or the listings grid.
//
// Previously the script was hard-coded synchronously in index.html, so *every*
// page (home included) paid for it before first paint. This module injects it
// on demand instead and resolves once `window.google.maps` is ready, so the
// consuming components can `await loadGoogleMaps()` in their mount hook.
//
// The injection is memoised: concurrent callers share one <script> and one
// promise. Callers should keep their existing `if (!window.google) return`
// guards as a belt-and-braces fallback (blocked network, tests, etc.).

const API_KEY = 'AIzaSyCH2BcaOkNgjYZqigrewdxXyz5atnsfdRo';
const SRC = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${API_KEY}`;

let loadPromise = null;

/**
 * @returns {Promise<typeof window.google>} resolves with the google namespace
 *   once maps is ready; rejects if the script fails to load.
 */
export function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google && window.google.maps) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    // Reuse a script another module may already have appended.
    const existing = document.querySelector('script[data-google-maps]');
    const onReady = () => resolve(window.google);
    if (existing) {
      existing.addEventListener('load', onReady);
      existing.addEventListener('error', reject);
      if (window.google && window.google.maps) onReady();
      return;
    }
    const s = document.createElement('script');
    s.src = SRC;
    s.async = true;
    s.defer = true;
    s.setAttribute('data-google-maps', '');
    s.addEventListener('load', onReady);
    s.addEventListener('error', (e) => {
      s.remove();         // drop the failed tag so a retry injects a fresh one
      loadPromise = null; // allow a later retry
      reject(e);
    });
    document.head.appendChild(s);
  });

  return loadPromise;
}
